import React, {useEffect, useRef} from "react"
import gsap from "gsap";
import Block from "./Elements/Block";

import styles from "./Introduction.module.scss";
import Button from "./Global/Button";
import ImageText from "./Elements/ImageText";

import image from "../images/masthead-image.jpg";
import imageRet from "../images/masthead-image@2x.jpg";
import imageWebp from "../images/masthead-image.webp";
import imageWebpRet from "../images/masthead-image@2x.webp";
import imageMobile from "../images/masthead-mobile.jpg";

import src from "../images/masthead-bg.jpg";
import srcRet from "../images/masthead-bg@2x.jpg";
import webp from "../images/masthead-bg.webp";
import webpRet from "../images/masthead-bg@2x.webp";

import Content from "./Elements/Content";
import EvolutionRemote from "./Graph/EvolutionRemote";

export default function Introduction () {
    const button = useRef(null);

    useEffect(() => {
        const buttonLink = button.current.querySelector('a');

        gsap.set(button.current, {
            width: buttonLink.getBoundingClientRect().width,
            height: buttonLink.getBoundingClientRect().height,
        });

        window.addEventListener('scroll', () => {
            const buttonRect = button.current.getBoundingClientRect();

            if (buttonRect.bottom < 0 && !button.current.classList.contains('animated')) {
                gsap.set(buttonLink, {
                    opacity: 0,
                    position: 'fixed',
                    bottom: 30,
                    right: 30,
                    zIndex: 9,
                    marginBottom: 0,
                    onComplete: () => {
                        gsap.to(buttonLink, .3, {
                            opacity: 1
                        });

                        button.current.classList.add('animated');
                    }
                })
            }
        })
    }, []);

    return (
        <Content>
            <Block classname={styles.Main} color={'white'} container={true} background={{src, srcRet, webp, webpRet}} mobile={imageMobile}>
                <h1>
                    <strong>
                        Télétravail,
                    </strong>
                    et conséquences
                </h1>

                <div className={styles.ButtonWrapper} ref={button}>
                    <Button text={'Investissez'} target={'_blank'} url={'https://ad.doubleclick.net/ddm/clk/522374461;330320686;a'}/>
                </div>

                <p className={'large size-6'}>
                    Quand l’exception devient la norme… Alors que le télétravail était une pratique peu répandue il y a encore quelques années, la pandémie de Covid-19 et la crise qui l’a accompagnée à partir de mars 2020 ont bouleversé les comportements au travail partout dans le monde. Pour freiner les contaminations, une myriade de pays ont mis en place des confinements et des couvre-feux, demandant au passage aux entreprises qui le pouvaient de mettre en place le travail à la maison pour leurs salariés, parfois à 100% du temps, pendant de longues semaines. Selon l’OCDE, en Australie, en France et au Royaume-Uni, près de 50% des employés ont travaillé de chez eux durant les confinements en 2020. Au Japon, qui n’a pas instauré de confinement généralisé, le taux de télétravail est passé de 10% à 28% entre décembre 2019 et mai 2020. Les nombreuses vagues successives de la pandémie et les restrictions sanitaires ont renforcé ce dispositif, et ancré cette manière de travailler, rebattant parfois les cartes dans l’organisation du travail, avec de plus en plus d’interactions virtuelles.
                </p>

                <ImageText image={{src: image, srcRet: imageRet, webp: imageWebp, webpRet: imageWebpRet}} classname={[styles.Content].join(' ')} radius={true}>
                    <p>
                        Qui dit télétravail, dit aussi plateformes de travail collaboratif. Zoom, Teams, Slack, Meet... Devenus des compagnons indispensables pour poursuivre leur activité professionnelle, de nombreux outils permettent aujourd’hui aux employés de continuer à travailler en équipe, partager des documents ou stocker des fichiers dans le cloud. Une véritable aubaine donc pour les services de communication collaboratifs, les plateformes de technologie cloud, de conférence et de gestion de projets. Le marché des vidéoconférences devrait d’ailleurs croître de 20% par an jusqu’en 2026 et celui du cloud de 20% chaque année jusqu’en 2025. Des solutions de digital workplace donnent également la possibilité aux télétravailleurs d’être avertis en temps réel, que ce soit sur des appareils de bureau ou mobiles, pour rester informés des dernières nouvelles de l’entreprise et des activités de leurs équipes. Des propositions de réalité virtuelle ou de réalité augmentée pour améliorer les échanges entre les collaborateurs se font également de plus en plus nombreuses…
                    </p>

                    <p>
                        Mais ce qui était au départ une solution temporaire a finalement été un changement de paradigme complet, puisque 74% des entreprises déclaraient déjà début avril 2020 qu’elles allaient assigner à certains employés des postes de télétravail permanents, d’après une enquête réalisée par le cabinet Gartner. En France, une étude menée par les ministères du Travail et de la Santé et le syndicat Ugict-CGT auprès de 15.000 cadres, managers, professions intermédiaires et employés du tertiaire a révélé que 98% des salariés interrogés souhaitent poursuivre le télétravail après la crise sanitaire. Les motivations sont nombreuses : diminution du temps de trajet, équilibre entre vie professionnelle et vie personnelle ou autonomie…
                    </p>
                </ImageText>
            </Block>

            <Block container={false} background={'white'} classname={[styles.Timeline, 'navigation--1'].join(' ')}>
                <div className="container">
                    <span className="subtitle">
                        Le télétravail durant la pandémie de Covid-19 en 2020

                        <small>(en % d’employés en Australie, Grande-Bretagne, Suède, France, Italie et Etats-Unis / Source : OCDE) </small>
                    </span>

                    <EvolutionRemote/>
                </div>
            </Block>
        </Content>
    )
}
